import { useState, useCallback } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import type { PromptTemplate } from '../../api';
import { togglePromptTemplateFavorite } from '../../api';
import { hasSystemPromptCategory } from '../../utils/systemPromptCategory';
import styles from './PromptCard.module.scss';

interface PromptCardProps {
  prompt: PromptTemplate;
  onUse: (content: string) => void;
  onApplyToAiSettings?: (content: string) => void;
  onEdit?: (prompt: PromptTemplate) => void;
  onDelete?: (prompt: PromptTemplate) => void;
  isOwn?: boolean;
}

export default function PromptCard({
  prompt,
  onUse,
  onApplyToAiSettings,
  onEdit,
  onDelete,
  isOwn = false,
}: PromptCardProps) {
  const [isFavorite, setIsFavorite] = useState(prompt.is_favorite);
  const [favoriteCount, setFavoriteCount] = useState(prompt.favorite_count);
  const [loading, setLoading] = useState(false);

  const handleLike = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (loading) return;
      setLoading(true);
      try {
        const res = await togglePromptTemplateFavorite(prompt.id);
        setIsFavorite(res.is_favorite);
        setFavoriteCount(res.favorite_count);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    },
    [prompt.id, loading]
  );

  const primaryCategory = prompt.categories?.[0];
  const showAiSettingsAction =
    Boolean(onApplyToAiSettings) && hasSystemPromptCategory(prompt.categories);

  return (
    <article
      className={styles.cardWrapper}
      data-category={primaryCategory?.id}
      style={
        primaryCategory
          ? ({ '--category-color': primaryCategory.color } as React.CSSProperties)
          : undefined
      }
    >
      <div className={styles.cardInner}>
        <div className={styles.headerRow}>
          <div className={styles.categories}>
            {prompt.categories?.map((cat) => (
              <span
                key={cat.id}
                className={styles.category}
                style={{ backgroundColor: cat.color, color: '#fff' }}
              >
                {cat.title}
              </span>
            ))}
          </div>
          <button
            type="button"
            className={`${styles.likeButton} ${isFavorite ? styles.likeButtonLiked : ''}`}
            onClick={handleLike}
            disabled={loading}
          >
            {isFavorite ? (
              <FavoriteIcon className={styles.likeIcon} />
            ) : (
              <FavoriteBorderIcon className={styles.likeIcon} />
            )}
            {favoriteCount}
          </button>
        </div>
        <div className={styles.contentSwap}>
          <div className={styles.previewBlock}>
            <h3 className={styles.title}>{prompt.title}</h3>
            {prompt.description && <p className={styles.description}>{prompt.description}</p>}
          </div>
          <div className={styles.promptBlock}>
            <div className={styles.promptText}>{prompt.content}</div>
          </div>
        </div>
        <div className={styles.actions}>
          <div className={styles.primaryActions}>
            <button type="button" className={styles.button} onClick={() => onUse(prompt.content)}>
              Использовать
              <ArrowForwardIcon className={styles.buttonIcon} />
            </button>
            {showAiSettingsAction && (
              <button
                type="button"
                className={styles.buttonSecondary}
                onClick={() => onApplyToAiSettings?.(prompt.content)}
                title="Подставить в системный промпт на странице «Настройки ИИ»"
              >
                В настройки ИИ
                <SettingsOutlinedIcon className={styles.buttonIcon} />
              </button>
            )}
          </div>
          {isOwn && (
            <span className={styles.cardActions}>
              {onEdit && (
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(prompt);
                  }}
                  title="Редактировать"
                >
                  <EditIcon fontSize="small" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.deleteButton}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(prompt);
                  }}
                  title="Удалить"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              )}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
